import { Injectable, inject } from '@angular/core';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { environment } from '../../environments/environment';
import { Capacitor } from '@capacitor/core';
import { PushTokenService, UserService } from '../services/rest.service';
import { AppLinkService } from './app-link.service';
import { Router } from '@angular/router';
import { Calendar } from '@awesome-cordova-plugins/calendar/ngx';
import { Platform } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
export type NotificationData = {
  id?: number; // add this
  notification_id?: string;
  type?: string; // optional now
  trip_id?: string;
  message_id?: string;
  promo_id?: string;
  user_id?: string;
  body: string;
  avatar: string;
  title?: string;
  data: {
    id?: number;
    notification_id?: string;
    type: string;
    trip_id?: number;
    reservation_id?: number;
    status?: 'accepted' | 'rejected' | 'pending' | 'canceled' | 'cancelled' | string;
    start_point?: string;
    destination?: string;
    passenger_name?: string;
    title?: string;
    deleted?: boolean;
    update?: boolean;
    created_at?: string;
    created_at_str?: string;
    start_time?: string; // ISO date string
    start_time_raw?: string;
    end_time?: string; // ISO date string
    end_time_raw?: string;
  };
  read: boolean;
};

type CalendarTarget = 'auto' | 'native' | 'web';

interface AddTripsToCalendarOptions {
  target?: CalendarTarget;
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private messaging: Messaging = inject(Messaging);
  private sPlatform: string = 'web';
  _unreadCount = 0;
  public isInitialized = false;
  private readonly storageKeyPrefix = 'notifications';
  /** User whose notifications are currently in memory / storage. */
  private boundUserId: number | null = null;
  private notificationsSubject = new BehaviorSubject<NotificationData[]>([]);
  private markReadRequests = new Set<string>();

  /** Emits live reservation or trip updates to active components */
  private liveEventsSubject = new BehaviorSubject<any>(null);
  liveEvents$ = this.liveEventsSubject.asObservable();
  emitLiveEvent(event: any) {
    this.liveEventsSubject.next(event);
  }

  notifications$ = this.notificationsSubject.asObservable();
  notificationsHydrated = false;

  constructor(
    private platform: Platform,
    private calendar: Calendar,
    public pushTokenService: PushTokenService,
    private router: Router,
    private appLink: AppLinkService,
    private toastController: ToastController,
    private translate: TranslateService,
    private userService: UserService,
  ) {
    // Only hydrate when a session already exists (avoid leaking prior account cache).
    if (this.currentSessionUserId()) {
      void this.getNotificationsFromDatabase();
    }
  }
  /** Holds all notifications */
  public notifications: NotificationData[] = [];

  incrementUnread() {
    this._unreadCount++;
  }

  resetUnread() {
    this._unreadCount = 0;
  }

  /** Expose unread count dynamically */
  get unreadCount(): number {
    return this._unreadCount;
  }

  /** Initialize push notifications after login */
async initPush(userId: any) {
  const normalizedUserId = Number(userId);
  if (!Number.isInteger(normalizedUserId) || normalizedUserId <= 0) {
    console.warn('[Push] Skipping initialization until a user is logged in.');
    return;
  }

  // Account switch: drop previous user's alerts immediately.
  if (this.boundUserId && this.boundUserId !== normalizedUserId) {
    this.clearLocalNotifications();
  }

  void this.getNotificationsFromDatabase();

  if (this.currentToken) {
    console.log('[Push] Already initialized.');
    this.sendTokenToBackend(normalizedUserId, this.currentToken, this.sPlatform);
    return;
  }

  if (Capacitor.isNativePlatform()) {
    await this.initNativePush(normalizedUserId);
  } else {
    this.sPlatform = 'web';
    await this.initWebPush(normalizedUserId);
  }
}

private async initNativePush(userId: number) {
  console.log('[Push] Initializing native Firebase push');

  try {
    this.sPlatform = Capacitor.getPlatform(); // ios / android

    if (this.sPlatform === 'ios') {
      FirebaseMessaging.addListener('apnsTokenReceived', (event) => {
        console.log('[Push][iOS] APNs token received:', event.token);
      });
    }

    FirebaseMessaging.addListener('tokenReceived', (event) => {
      console.log('[Push] Refreshed FCM token:', event.token);
      if (event.token) {
        this.sendTokenToBackend(userId, event.token, this.sPlatform);
      }
    });

    const permission = await FirebaseMessaging.requestPermissions();
    console.log('[Push] Firebase permission:', permission);

    if (permission.receive !== 'granted') {
      console.warn('[Push] Permission not granted');
      return;
    }

    const result = await FirebaseMessaging.getToken();

    if (!result.token) {
      console.error('[Push] No FCM token returned');
      return;
    }

    console.log('[Push] ✅ FCM token:', result.token);

    this.sendTokenToBackend(userId, result.token, this.sPlatform);

    FirebaseMessaging.addListener('notificationReceived', async (event) => {
      console.log('[Push] 🔔 received:', event);
      const title = event.notification?.title ?? '';
      const body = event.notification?.body ?? '';
      const message = [title, body].filter(Boolean).join(': ');

      if (message && Capacitor.getPlatform() === 'android') {
        await this.presentToast(message);
      }

      await this.getNotificationsFromDatabase();
    });

    FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      console.log('[Push] 🎯 clicked:', event);
      this.handleNotificationAction(event.notification?.data as any, userId);
    });
  } catch (err) {
    console.error('[Push] Native Firebase push failed', err);
  }
}

  // ================= WEB PUSH =================
  private async initWebPush(userId: number) {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      console.warn('Web push notifications are not supported by this browser.');
      return;
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission denied');
        return;
      }
      this.currentToken = '';
      // Register service worker for Firebase
      const registration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
      );

      // Get web push token from Firebase
      const token = await getToken(this.messaging, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: registration,
      });
      this.currentToken = token;
      if (!token) {
        console.error('Failed to get web push token');
        return;
      }

      this.sendTokenToBackend(userId, token, this.sPlatform);

      // Listen to foreground messages
      onMessage(this.messaging, async (payload) => {
        // this.addNotification({
        //   title:
        //     payload.notification?.title ??
        //     payload.data?.['title'] ??
        //     'Notification',
        //   body: payload.notification?.body ?? payload.data?.['body'] ?? '',
        //   data: payload.data,
        //   time: new Date(),
        //   read: false,
        // });
        await this.getNotificationsFromDatabase();
      });
    } catch (err) {
      console.error('Web push initialization failed', err);
    }
  }

  private generateKey(notification: NotificationData) {
    return (
      notification.id ||
      (notification.trip_id
        ? `${notification.type}_${notification.trip_id}`
        : null) ||
      JSON.stringify(notification['data'])
    );
  }

  // public async addNotification(notification: NotificationData) {
  //   await this.getNotificationsFromDatabase();
  //   const data =
  //     typeof notification['data'] === 'string'
  //       ? JSON.parse(notification['data'])
  //       : notification['data'];
  //   const merged = { ...notification, ...data };
  //   const key = merged.id;
  //   const exists = key && this.notifications.some((n) => n.id === key);
  //   if (exists) return;
  //   this.notifications.unshift(merged);
  //   this.saveToStorage();
  //   this.notificationsSubject.next(this.notifications);
  // }
  getNotifications(): any[] {
    return this.notifications;
  }
  // public markAsRead(id: number | undefined) {
  //   const index = this.notifications.findIndex((n) => n.id === id);
  //   if (index !== -1) {
  //     this.notifications[index]['read'] = true;
  //     this.saveToStorage();

  //     // 👇 emit a new array reference to trigger Angular change detection
  //     this.notificationsSubject.next([...this.notifications]);
  //   }
  // }
  public markAsRead(
    notificationOrId: NotificationData | number | string | undefined,
  ) {
    const identifier = this.getNotificationReadIdentifier(notificationOrId);
    if (!identifier) return;

    const requestKey = identifier.id
      ? `id:${identifier.id}`
      : `notification_id:${identifier.notification_id}`;
    const index = this.notifications.findIndex((n) =>
      this.isSameNotification(n, identifier),
    );

    if (index !== -1) {
      this.notifications[index].read = true;

      // Update unread count
      this._unreadCount = this.notifications.filter((n) => !n.read).length;

      // Notify Angular components
      this.notificationsSubject.next([...this.notifications]);

      // Save locally
      this.saveToStorage();
    }

    if (this.markReadRequests.has(requestKey)) {
      return;
    }

    this.markReadRequests.add(requestKey);

    // --- SEND READ FLAG TO BACKEND ---
    this.pushTokenService.markNotificationRead(identifier).subscribe({
      next: () => console.log('Notification marked as read in DB'),
      error: (err) => {
        this.markReadRequests.delete(requestKey);
        console.error('Failed to mark notification as read in DB', err);
      },
    });
  }

  public markAllAsRead() {
    this.notifications = this.notifications.map((n) => ({ ...n, read: true }));
    this.saveToStorage();
    this.notificationsSubject.next(this.notifications); // 👈 notify again
  }

  async markAllAsReadBackend() {
    try {
      await this.pushTokenService.markAllRead().toPromise();
      this.notifications = this.notifications.map((n) => ({
        ...n,
        read: true,
      }));
      this.resetUnread(); // set unread count to 0
      this.notificationsSubject.next([...this.notifications]);
      this.saveToStorage();
    } catch (err) {
      console.error('Failed to mark notifications as read:', err);
    }
  }

  // ================= HANDLE NOTIFICATION ACTION =================

  public handleNotificationAction(
    notification: NotificationData | undefined,
    userId: number | null,
  ) {
    if (!notification) return;
    const data = this.getMergedNotificationData(notification);

    this.markAsRead(notification);

    // Use the parsed data to get the type
    const type = data?.type ?? 'unknown'; // const data = notification['data'] ?? {};

    switch (type) {
      case 'trip_update':
        if (data.deleted) {
          const message =
            data.message ||
            this.translate.instant('NOTIFICATIONS.TRIP_CANCELED');

          // this.addNotification({
          //   title: this.translate.instant('NOTIFICATIONS.TRIP_CANCELED_TITLE'),
          //   id: data.id,
          //   body: message,
          //   time: new Date(),
          //   read: data.read,
          //   data,
          // });

          // if (notification.id) {
          //   this.markAsRead(notification.id);
          // }

          // 👉 feedback toast
          this.presentToast(message);

          return;
        }

        // Active trips → navigate to trip details
        if (data.update) {
          // 1️⃣ Add to notifications list if not already there
          // this.addNotification({
          //   title: data.title ?? 'Trip Updated',
          //   id: data.id,
          //   body: `The trip from ${data.start_point} to ${data.destination} has been updated.`,
          //   time: new Date(),
          //   read: data.read, // initially unread
          //   data, // store original data
          // });

          // // 2️⃣ Optional: mark as read immediately if clicked
          // if (notification.id) {
          //   this.markAsRead(notification.id);
          // }

          this.openTripOrUnavailableToast(data);
        }

        // Active trips → navigate to trip details
        if (this.getTripIdFromNotificationData(data)) {
          // 1️⃣ Add to notifications list if not already there
          // this.addNotification({
          //   title: data.title ?? 'Trip Update',
          //   id: data.id,
          //   body: data.message ?? 'Your trip has an update.',
          //   time: new Date(),
          //   read: data.read, // initially unread
          //   data, // store original data
          // });

          // // 2️⃣ Optional: mark as read immediately if clicked
          // if (notification.id) {
          //   this.markAsRead(notification.id);
          // }

          this.openTripOrUnavailableToast(data);
        }
        break;
      case 'reservation_status': {
        const status = this.normalizeReservationStatus(data.status);
        const openSection = this.getTripDetailsOpenSection(status);
        const isDriver = this.isNotificationForTripDriver(data, userId);

        if (isDriver && openSection) {
          this.openTripOrUnavailableToast(data, openSection);
          if (status === 'accepted') {
            this.emitLiveEvent({
              type: 'reservation_status',
              status,
              trip_id: data.trip_id,
              user_id: data.user_id,
            });
          }
        } else if (status === 'accepted' || status === 'rejected') {
          this.openReservedTripOrUnavailableToast(data);
          if (status === 'accepted') {
            this.emitLiveEvent({
              type: 'reservation_status',
              status,
              trip_id: data.trip_id,
              user_id: data.user_id,
            });
          }
        } else if (openSection) {
          this.openTripOrUnavailableToast(data, openSection);
        }
        break;
      }
      // ---------------- Reservation Canceled by Passenger (legacy type) ----------------
      case 'reservation_canceled':
        this.openTripOrUnavailableToast(data, 'canceled');
        return;

      case 'subscription':
        console.log('subscription notification', data);

        // // Add notification to your list
        // this.addNotification({
        //   title: data.title ?? 'New Trip Available!',
        //   id: data.id,
        //   body:
        //     data.message ||
        //     (data.start_point && data.destination
        //       ? `A trip from ${data.start_point} to ${data.destination} matches your subscription!`
        //       : 'New trip available!'),
        //   time: new Date(data.created_at || undefined),
        //   read: data.read,
        //   data,
        // });

        // Mark as read immediately
        // if (notification.id) {
        //   this.markAsRead(notification.id);
        // }

        // Optional: navigate user to the trip details page
        this.openTripOrUnavailableToast(data, 'subscription');

        break;
      case 'admin_document_submission':
        this.navigateToAdminSubmission(data);
        break;
      case 'document_verification_status':
        this.navigateToDocumentVerificationStatus(data);
        break;
      default:
        this.navigateToTripFromNotification(data);
        console.log('Unknown notification type', notification);
    }
  }

  private navigateToDocumentVerificationStatus(data: any): boolean {
    if (data?.document_type === 'car_license') {
      this.router.navigate(['/profile', 'cars'], {
        queryParams: {
          document: 'car_license',
          ...(data?.car_id ? { car_id: data.car_id } : {}),
        },
      });
      return true;
    }

    // Use profile shell (has ion-content) — bare /user-details breaks scrolling.
    this.router.navigate(['/profile', 'personal'], {
      queryParams: {
        document: data?.document_type || 'identity',
      },
    });
    return true;
  }

  private navigateToAdminSubmission(data: any): boolean {
    const submission = this.adminSubmissionKey(data);
    const queryParams: Record<string, string> = {
      tab: 'submissions',
      ...(submission ? { submission } : {}),
    };
    this.appLink.opened$.next({ path: '/admin', queryParams });
    this.router.navigate(['/admin'], {
      queryParams,
      onSameUrlNavigation: 'reload',
    });

    return true;
  }

  private adminSubmissionKey(data: any): string | null {
    if (typeof data?.submission_key === 'string' && data.submission_key.trim()) {
      return data.submission_key.trim();
    }

    const type = data?.submission_type;

    if (type === 'car_license') {
      const carId = this.toNumberOrNull(data?.car_id);
      return carId ? `car-${carId}-license` : null;
    }

    const userId = this.toNumberOrNull(data?.submitter_id ?? data?.user_id);
    if (!userId) return null;

    if (type === 'identity') {
      return `user-${userId}-identity`;
    }

    if (type === 'driver_license') {
      return `user-${userId}-driver-license`;
    }

    return null;
  }

  private documentVerificationLabelKey(documentType?: string): string {
    switch (documentType) {
      case 'identity':
        return 'ADMIN.IDENTITY_PHOTO';
      case 'driver_license':
        return 'ADMIN.DRIVER_LICENSE';
      case 'car_license':
        return 'ADMIN.CAR_LICENSE';
      default:
        return 'ADMIN.DOCUMENT_PHOTOS';
    }
  }

  private async presentToast(message: string) {
    const toast = await this.toastController.create({
      message,
      duration: 2500,
      position: 'top',
      color: 'primary',
    });
    await toast.present();
  }

  private normalizeReservationStatus(status: unknown): string {
    const normalized = String(status || '')
      .trim()
      .toLowerCase();
    return normalized === 'cancelled' ? 'canceled' : normalized;
  }

  private getTripDetailsOpenSection(status: string): string | undefined {
    if (['accepted', 'pending', 'rejected', 'canceled'].includes(status)) {
      return status;
    }
    return undefined;
  }

  private isNotificationForTripDriver(
    data: any,
    userId: number | null,
  ): boolean {
    if (!userId) {
      return false;
    }

    const driverId =
      this.toNumberOrNull(data?.user_id) ??
      this.toNumberOrNull(data?.driver_id);

    return driverId === userId;
  }

  private openTripOrUnavailableToast(data: any, openSection?: string): boolean {
    if (this.isNotificationTripEnded(data)) {
      this.presentToast(this.translate.instant('NOTIFICATIONS.TRIP_ENDED'));
      return false;
    }

    const didNavigate = this.navigateToTripFromNotification(data, openSection);

    if (!didNavigate) {
      this.presentToast(
        this.translate.instant('NOTIFICATIONS.TRIP_DETAILS_UNAVAILABLE'),
      );
    }

    return didNavigate;
  }

  private openReservedTripOrUnavailableToast(data: any): boolean {
    const didNavigate = this.navigateToReservedTripFromNotification(data);

    if (!didNavigate) {
      this.presentToast(
        this.translate.instant('NOTIFICATIONS.TRIP_DETAILS_UNAVAILABLE'),
      );
    }

    return didNavigate;
  }

  private navigateToTripFromNotification(data: any, openSection?: string): boolean {
    if (this.isNotificationTripEnded(data)) {
      return false;
    }

    const tripId = this.getTripIdFromNotificationData(data);

    if (!tripId) {
      return false;
    }

    this.router.navigate(['/trip-details'], {
      queryParams: {
        trip_id: tripId,
        from: 'notification',
        ...(openSection ? { openSection } : {}),
      },
    });

    return true;
  }

  private navigateToReservedTripFromNotification(data: any): boolean {
    const reservationId = this.getReservationIdFromNotificationData(data);

    if (!reservationId) {
      return false;
    }

    this.router.navigate(['/reserved-trips'], {
      queryParams: {
        Restrip_id: reservationId,
        from: 'notification',
      },
    });

    return true;
  }

  private getReservationIdFromNotificationData(data: any): string | number | null {
    const candidates = [
      data?.reservation_id,
      data?.reservationId,
      data?.reserved_trip_id,
      data?.reservedTripId,
      data?.Restrip_id,
      data?.restrip_id,
      data?.reservation?.id,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
        return candidate;
      }
      if (typeof candidate === 'string' && candidate.trim() && /^\d+$/.test(candidate.trim())) {
        return candidate.trim();
      }
    }

    return null;
  }

  private getTripIdFromNotificationData(data: any): string | number | null {
    const candidates = [
      data?.trip_id,
      data?.tripId,
      data?.offered_trip_id,
      data?.offeredTripId,
      data?.offeredTrip?.id,
      data?.trip?.id,
      typeof data?.trip === 'object' ? null : data?.trip,
      data?.reservation?.trip_id,
      data?.reservation?.tripId,
      data?.reservation?.trip,
      data?.reservation?.offered_trip_id,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate;
      }

      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }

    return null;
  }

  private isNotificationTripEnded(data: any): boolean {
    const dateValue =
      data?.end_time_raw ??
      data?.endTimeRaw ??
      data?.end_time ??
      data?.endTime ??
      data?.trip?.end_time ??
      data?.reservation?.end_time ??
      data?.start_time_raw ??
      data?.startTimeRaw ??
      data?.start_time ??
      data?.startTime ??
      data?.trip?.start_time ??
      data?.reservation?.start_time;
    const date = this.toValidDate(dateValue);

    return !!date && date.getTime() < Date.now();
  }

  private getNotificationReadIdentifier(
    notificationOrId: NotificationData | number | string | undefined,
  ): { id?: number; notification_id?: string } | null {
    if (!notificationOrId) {
      return null;
    }

    if (typeof notificationOrId === 'number') {
      return { id: notificationOrId };
    }

    if (typeof notificationOrId === 'string') {
      return /^\d+$/.test(notificationOrId)
        ? { id: Number(notificationOrId) }
        : { notification_id: notificationOrId };
    }

    const data = this.getMergedNotificationData(notificationOrId);
    const id = this.toNumberOrNull(notificationOrId.id ?? data?.id);

    if (id) {
      return { id };
    }

    const notificationId =
      notificationOrId.notification_id ?? data?.notification_id;

    return notificationId ? { notification_id: String(notificationId) } : null;
  }

  private isSameNotification(
    notification: NotificationData,
    identifier: { id?: number; notification_id?: string },
  ): boolean {
    if (identifier.id && notification.id === identifier.id) {
      return true;
    }

    const data = this.getMergedNotificationData(notification);
    return !!(
      identifier.notification_id &&
      data?.notification_id &&
      String(data.notification_id) === identifier.notification_id
    );
  }

  private getNotificationData(notification: any): any {
    if (!notification?.data) {
      return notification || {};
    }

    if (typeof notification.data === 'string') {
      try {
        return JSON.parse(notification.data);
      } catch {
        return {};
      }
    }

    return notification.data;
  }

  private getMergedNotificationData(notification: any): any {
    const data = this.getNotificationData(notification);
    return { ...(notification || {}), ...(data || {}) };
  }

  private toNumberOrNull(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && /^\d+$/.test(value)) {
      return Number(value);
    }

    return null;
  }

  private toValidDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(String(value));

    return Number.isNaN(date.getTime()) ? null : date;
  }

  // ================= SEND TOKEN TO BACKEND =================

  public currentToken: string | null = null;

  private sendTokenToBackend(userId: number, token: string, platform: string) {
    this.currentToken = token; // <-- keep reference for logout
    this.pushTokenService
      .saveToken({ user_id: userId, device_token: token, platform })
      .subscribe({
        next: () => console.log('Device token saved via service'),
        error: (err) =>
          console.error('Failed to save device token via service', err),
      });
  }

  async addTripToCalendar(notification: any) {
    await this.addTripsToCalendar([notification]);
  }

  async addTripsToCalendar(
    notifications: any[],
    options: AddTripsToCalendarOptions = {},
  ) {
    console.log('addTripsToCalendar called', notifications);

    const events = (notifications || [])
      .map((notification) => this.toCalendarEvent(notification))
      .filter((event): event is NonNullable<typeof event> => !!event);

    if (!events.length) {
      console.warn('Missing start_time in calendar trips');
      return;
    }

    const addToCalendarLabel = this.translate.instant('ADD_TRIP.addCalender');
    const icsFileLabel = this.translate.instant('ADD_TRIP.ICAL_FILE');

    await this.platform.ready();

    const target = options.target ?? 'auto';
    const isMobile = this.platform.is('hybrid') || Capacitor.isNativePlatform();

    if (target === 'native' && !isMobile) {
      throw new Error('Native calendar is unavailable outside the mobile app.');
    }

    if (target !== 'web' && isMobile) {
      let lastError: unknown = null;

      for (const event of events) {
        try {
          await this.calendar.createEventInteractively(
            event.title,
            event.location,
            event.notes,
            event.startTime,
            event.endTime,
          );
          console.log('Event added to calendar');
        } catch (err) {
          console.error('Failed to add event', err);
          lastError = err;
        }
      }

      if (lastError) {
        throw lastError;
      }

      return;
    }

    const container = document.getElementById('calendar-container');
    if (!container) return;

    container.innerHTML = '';

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const pad = (value: number) => value.toString().padStart(2, '0');
    const localDate = (date: Date) =>
      `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}00`;
    const utcDate = (date: Date) =>
      date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
    const escapeIcs = (value: string) =>
      value
        .replace(/\\/g, '\\\\')
        .replace(/,/g, '\\,')
        .replace(/;/g, '\\;')
        .replace(/\n/g, '\\n');
    const firstEvent = events[0];
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: firstEvent.title,
      dates: `${localDate(firstEvent.startTime)}/${localDate(firstEvent.endTime)}`,
      details: firstEvent.notes,
      location: firstEvent.location,
      ctz: timeZone,
    });
    const googleUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;
    const outlookParams = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject: firstEvent.title,
      startdt: firstEvent.startTime.toISOString(),
      enddt: firstEvent.endTime.toISOString(),
      body: firstEvent.notes,
      location: firstEvent.location,
    });
    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?${outlookParams.toString()}`;
    const yahooParams = new URLSearchParams({
      v: '60',
      title: firstEvent.title,
      st: localDate(firstEvent.startTime),
      et: localDate(firstEvent.endTime),
      desc: firstEvent.notes,
      in_loc: firstEvent.location,
    });
    const yahooUrl = `https://calendar.yahoo.com/?${yahooParams.toString()}`;
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Bidarbak//Trip Calendar//EN',
      ...events.flatMap((event, index) => [
        'BEGIN:VEVENT',
        `UID:${Date.now()}-${index}@bidarbak`,
        `DTSTAMP:${utcDate(new Date())}`,
        `DTSTART:${utcDate(event.startTime)}`,
        `DTEND:${utcDate(event.endTime)}`,
        `SUMMARY:${escapeIcs(event.title)}`,
        `DESCRIPTION:${escapeIcs(event.notes)}`,
        `LOCATION:${escapeIcs(event.location)}`,
        'END:VEVENT',
      ]),
      'END:VCALENDAR',
    ].join('\r\n');
    const icsUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;

    const menu = document.createElement('div');
    menu.className = 'calendar-menu';
    menu.style.cssText = 'position:relative;width:100%;font-family:inherit;';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'calendar-menu-trigger';
    const triggerIcon = document.createElement('ion-icon');
    triggerIcon.setAttribute('aria-hidden', 'true');
    triggerIcon.setAttribute('name', 'calendar-outline');
    const triggerLabel = document.createElement('span');
    triggerLabel.textContent =
      events.length > 1 ? `${addToCalendarLabel} (${events.length})` : addToCalendarLabel;
    trigger.append(triggerIcon, triggerLabel);
    trigger.style.cssText =
      'align-items:center;background:#6b7280;border:0;border-radius:10px;color:#fff;cursor:pointer;display:flex;font:inherit;font-size:15px;font-weight:600;gap:8px;height:44px;justify-content:center;line-height:1;margin:0;padding:0 12px;width:100%;';

    const list = document.createElement('div');
    list.className = 'calendar-menu-list';
    list.style.cssText =
      'background:#fff;border-radius:10px;bottom:50px;box-shadow:0 10px 24px rgba(0,0,0,.25);display:none;flex-direction:column;left:0;overflow:hidden;position:absolute;width:100%;z-index:1000;';

    const addOption = (label: string, href: string, download?: string) => {
      const option = document.createElement('a');
      option.textContent = label;
      option.href = href;
      option.target = download ? '_self' : '_blank';
      option.rel = 'noopener';
      if (download) option.download = download;
      option.style.cssText =
        'color:#111827;display:block;font-size:15px;padding:14px 16px;text-align:center;text-decoration:none;';
      option.addEventListener('mouseenter', () => (option.style.background = '#f3f4f6'));
      option.addEventListener('mouseleave', () => (option.style.background = '#fff'));
      option.addEventListener('click', () => (list.style.display = 'none'));
      list.appendChild(option);
    };

    if (events.length === 1) {
      addOption('Google', googleUrl);
      addOption(icsFileLabel, icsUrl, 'bidarbak-trip.ics');
      addOption('Outlook.com', outlookUrl);
      addOption('Yahoo', yahooUrl);
    } else {
      addOption(icsFileLabel, icsUrl, 'bidarbak-trips.ics');
    }

    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      list.style.display = list.style.display === 'flex' ? 'none' : 'flex';
    });
    document.addEventListener('click', () => (list.style.display = 'none'));

    menu.append(trigger, list);
    container.appendChild(menu);
  }

  private toCalendarEvent(notification: any) {
    if (!notification || !notification.start_time) {
      return null;
    }

    const startTime = new Date(notification.start_time);
    if (Number.isNaN(startTime.getTime())) {
      return null;
    }

    const parsedEndTime = notification.end_time
      ? new Date(notification.end_time)
      : null;
    const endTime =
      parsedEndTime && !Number.isNaN(parsedEndTime.getTime())
        ? parsedEndTime
        : new Date(startTime.getTime() + 60 * 60 * 1000);
    const title =
      notification.trip_name ??
      this.translate.instant('ADD_TRIP.CALENDAR_TITLE', {
        start: notification.start_point || '',
        destination: notification.destination || '',
      });
    const location = notification.start_point || '';
    const notes = this.translate.instant('ADD_TRIP.CALENDAR_DESTINATION_NOTE', {
      destination: notification.destination || '',
    });

    return { title, location, notes, startTime, endTime };
  }

  /** 🔒 Save notifications to localStorage (scoped per user). */
  private saveToStorage() {
    const userId = this.boundUserId || this.currentSessionUserId();
    if (!userId) return;
    localStorage.setItem(
      this.storageKeyFor(userId),
      JSON.stringify(this.notifications),
    );
  }

  /** Drop in-memory + cached alerts (call on logout / account switch). */
  clearLocalNotifications(): void {
    const previousUserId = this.boundUserId;
    this.notifications = [];
    this._unreadCount = 0;
    this.notificationsHydrated = false;
    this.boundUserId = null;
    this.markReadRequests.clear();
    this.notificationsSubject.next([]);

    try {
      if (previousUserId) {
        localStorage.removeItem(this.storageKeyFor(previousUserId));
      }
      // Legacy shared key from older builds.
      localStorage.removeItem(this.storageKeyPrefix);
    } catch {
      // ignore storage errors
    }
  }

  private currentSessionUserId(): number | null {
    const raw = localStorage.getItem('userId');
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  private storageKeyFor(userId: number): string {
    return `${this.storageKeyPrefix}:${userId}`;
  }

  /** 🔓 Load notifications from API for the logged-in user only. */
  async getNotificationsFromDatabase(): Promise<void> {
    const userId = this.currentSessionUserId();
    if (!userId) {
      this.clearLocalNotifications();
      this.notificationsHydrated = true;
      this.notificationsSubject.next([]);
      return;
    }

    if (this.boundUserId && this.boundUserId !== userId) {
      this.clearLocalNotifications();
    }
    this.boundUserId = userId;

    try {
      const response: any = await this.userService
        .getNotifications()
        .toPromise();
      const rawNotifications = response.notifications || [];

      // Another login may have happened while the request was in flight.
      if (this.currentSessionUserId() !== userId) {
        return;
      }

      this.notifications = rawNotifications.map((notif: any) => {
        // 1️⃣ Parse data if it's a string
        if (notif?.data && typeof notif.data === 'string') {
          try {
            notif.data = JSON.parse(notif.data);
          } catch (e) {
            console.warn('Invalid notification data JSON', notif.data);
            notif.data = {};
          }
        }

        // 2️⃣ Create a localized date string
        if (notif.data?.created_at) {
          const date = new Date(notif.data.created_at);
          const lang = this.translate.currentLang || 'en'; // AR, FR, EN
          notif.data.created_at_str = date.toLocaleString(lang, {
            year: 'numeric',
            month: 'short', // month in locale format
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
          });
        }

        if (notif.data?.start_time) {
          notif.data.start_time_raw =
            notif.data.start_time_raw || notif.data.start_time;
        }

        if (notif.data?.end_time) {
          notif.data.end_time_raw = notif.data.end_time_raw || notif.data.end_time;
        }

        // 3️⃣ Set translation keys (not translated text yet)
        const type = notif.data?.type;
        switch (type) {
          case 'subscription':
            notif.title = 'notifications.new_trip_title';
            notif.body = 'notifications.new_trip_body';
            break;

          case 'trip_update':
            notif.title = 'notifications.TRIP_CANCELED_TITLE';
            notif.body = 'notifications.TRIP_CANCELED';
            break;

          case 'reservation_canceled':
            notif.data = notif.data || {};
            notif.data.passenger_name =
              notif.data.passenger_name ||
              this.translate.instant('notifications.A_PASSENGER');
            notif.title = 'notifications.PASSENGER_CANCELED_TITLE';
            notif.body = 'notifications.PASSENGER_CANCELED';
            break;

          case 'reservation_status':
            notif.data = notif.data || {};
            if (
              notif.data.status === 'pending' ||
              notif.data.status === 'canceled' ||
              notif.data.status === 'cancelled'
            ) {
              notif.data.passenger_name =
                notif.data.passenger_name ||
                this.translate.instant('notifications.A_PASSENGER');
            }
            if (notif.data.status === 'accepted') {
              notif.title = 'notifications.RESERVATION_ACCEPTED_TITLE';
              notif.body = 'notifications.RESERVATION_ACCEPTED_BODY';
            } else if (notif.data.status === 'rejected') {
              notif.title = 'notifications.RESERVATION_REJECTED_TITLE';
              notif.body = 'notifications.RESERVATION_REJECTED_BODY';
            } else if (notif.data.status === 'pending') {
              notif.title = 'notifications.RESERVATION_PENDING_TITLE';
              notif.body = 'notifications.RESERVATION_PENDING_BODY';
            } else if (
              notif.data.status === 'canceled' ||
              notif.data.status === 'cancelled'
            ) {
              notif.title = 'notifications.RESERVATION_CANCELED_TITLE';
              notif.body = 'notifications.RESERVATION_CANCELED_BODY';
            }
            break;

          case 'admin_document_submission':
            notif.title = 'NOTIFICATIONS.ADMIN_DOCUMENT_SUBMISSION_TITLE';
            notif.body = 'NOTIFICATIONS.ADMIN_DOCUMENT_SUBMISSION_BODY';
            break;

          case 'document_verification_status':
            notif.data.document = this.translate.instant(
              this.documentVerificationLabelKey(notif.data?.document_type),
            );
            if (notif.data.status === 'approved') {
              notif.title = 'NOTIFICATIONS.DOCUMENT_APPROVED_TITLE';
              notif.body = 'NOTIFICATIONS.DOCUMENT_APPROVED_BODY';
            } else {
              notif.title = 'NOTIFICATIONS.DOCUMENT_REJECTED_TITLE';
              notif.body = 'NOTIFICATIONS.DOCUMENT_REJECTED_BODY';
            }
            break;

          default:
            notif.title = 'notifications.notification';
            notif.body = '';
        }

        return notif;
      });

      // One reservation → one status alert (keep newest Accepted/Rejected/Pending/Canceled).
      this.notifications = this.dedupeReservationStatusNotifications(
        this.notifications,
      );

      // Save locally
      this.saveToStorage();
    } catch (error) {
      console.error('Failed to load notifications from API', error);
      if (this.currentSessionUserId() !== userId) {
        return;
      }
      const data = localStorage.getItem(this.storageKeyFor(userId));
      this.notifications = data ? JSON.parse(data) : [];
      this.notifications = this.dedupeReservationStatusNotifications(
        this.notifications,
      );
    }

    this.notificationsHydrated = true;
    this.notificationsSubject.next(this.notifications);
    this._unreadCount = this.notifications.filter(
      (n) => n.read !== true,
    ).length;
  }

  private dedupeReservationStatusNotifications(
    notifications: NotificationData[],
  ): NotificationData[] {
    const seenReservationIds = new Set<string>();
    const result: NotificationData[] = [];

    for (const notif of notifications) {
      const data = (notif as any)?.data || {};
      if (data?.type !== 'reservation_status') {
        result.push(notif);
        continue;
      }

      const reservationId = String(
        data.reservation_id ??
          data.reservationId ??
          data.reserved_trip_id ??
          '',
      ).trim();

      if (!reservationId || reservationId === '0') {
        result.push(notif);
        continue;
      }

      if (seenReservationIds.has(reservationId)) {
        continue;
      }

      seenReservationIds.add(reservationId);
      result.push(notif);
    }

    return result;
  }
}
